import React, { forwardRef } from 'react';
import Spinner from './Spinner';
import { focusRing, motion } from './tokens';

// Accessible button primitive. variants ∈ primary | secondary | ghost | danger
// sizes ∈ sm | md | lg. Loading state disables interaction and announces the
// loader to assistive tech via aria-busy.
//
// Usage:
//   <Button onClick={save}>Save</Button>
//   <Button variant="ghost" size="sm" loading={isSaving}>Save</Button>
//   <Button as="a" href="/quote" variant="secondary">Get a quote</Button>

const VARIANTS = {
  primary:
    'bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800 shadow-sm disabled:bg-gray-200 disabled:text.subtle',
  secondary:
    'bg-white text.heading border border-gray-300 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 disabled:bg-gray-50 disabled:text.subtle disabled:border-gray-200',
  ghost:
    'bg-transparent text.body hover:bg-gray-100 active:bg-gray-200 disabled:text.subtle',
  danger:
    'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm disabled:bg-gray-200 disabled:text.subtle',
};

const SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-1.5',
  lg: 'h-12 px-6 text-base gap-2',
};

const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    leadingIcon: LeadingIcon = null,
    trailingIcon: TrailingIcon = null,
    className = '',
    children,
    type,
    ...rest
  },
  ref,
) {
  const isButton = Component === 'button';
  const isDisabled = disabled || loading;

  return (
    <Component
      ref={ref}
      // Default <button> to type="button" so it doesn't accidentally submit
      // forms it's nested in.
      type={isButton ? type || 'button' : undefined}
      disabled={isButton ? isDisabled : undefined}
      aria-disabled={!isButton ? isDisabled : undefined}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center font-semibold rounded-lg',
        motion,
        focusRing,
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" className="shrink-0 text-current" />
      ) : LeadingIcon ? (
        <LeadingIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
      ) : null}
      {children}
      {!loading && TrailingIcon && <TrailingIcon className="w-4 h-4 shrink-0" aria-hidden="true" />}
    </Component>
  );
});

export default Button;
